import {attractionHelpers} from "@/shared/utils/routing/attraction-helpers";
import {normalizeHrefPath} from "@/shared/utils/routing/normalize-href-path";

import {pageRoute} from "./routes/page.route";
import {type RentalQueryParams, rentalRoute} from "./routes/rental.route";
import {searchRoute} from "./routes/search.route";
import {TRIP_TABS, tripRoute} from "./routes/trip.route";
import type {RouteWithSlug, RouteWithThreeSlugs, RouteWithTwoSlugs, SimpleRoute} from "./types";

type CommunityLinkLike = {
    slug?: string | null;
    path?: string | null;
    record_type?: "community" | "franchiseReference";
};

type CommunityDetailRoute = RouteWithTwoSlugs & {
    urlFromCommunity: (destinationSlug: string, community: CommunityLinkLike) => string;
};

// Re-export query params for convenience
export type {RentalQueryParams};
export {TRIP_TABS};
export type {TripTab} from "./routes/trip.route";

/**
 * Type-safe routes configuration
 * Each route has: path, url(), and optionally parse()
 */
export const routes = {
    /**
     * Home page: /
     */
    home: {
        path: "/",
        url: () => "/",
    } as SimpleRoute,

    meeTheTeam: {
        path: "/meet-the-team",
        url: () => "/meet-the-team",
    } as SimpleRoute,
    /**
     * Blog routes
     */
    blog: {
        /**
         * Blog list page: /blog
         */
        list: {
            path: "/blog",
            url: () => "/blog",
        } as SimpleRoute,

        /**
         * Blog post detail page: /blog/[slug]
         */
        detail: {
            path: "/blog/[slug]",
            url: (slug: string) => (slug.startsWith("/") ? `/blog${slug}` : `/blog/${slug}`),
        } as RouteWithSlug,
    },

    /**
     * Checkout page: /checkout
     */
    checkout: {
        path: "/checkout/[slug]",
        url: (slug) => (slug.startsWith("/") ? `/checkout${slug}` : `/checkout/${slug}`),
    } as RouteWithSlug,

    /**
     * Thank you page: /thank-you/[reservationId]
     * Note: reservationId param actually receives checkoutId value
     */
    thankYou: {
        path: "/thank-you/[reservationId]",
        url: (checkoutId: string) => (checkoutId.startsWith("/") ? `/thank-you${checkoutId}` : `/thank-you/${checkoutId}`),
    } as RouteWithSlug,

    /**
     * Destinations routes
     */
    destinations: {
        /**
         * Franchise list page: /destinations/franchise
         */
        franchise: {
            path: "/destinations/",
            url: () => "/destinations/",
        } as SimpleRoute,

        /**
         * Destination detail page: /destinations/[slug]
         */
        detail: {
            path: "/[slug]",
            url: (slug: string) => (slug.startsWith("/") ? `${slug}` : `/${slug}`),
        } as RouteWithSlug,

        /**
         * Attraction list page: /destinations/[slug]/[attraction]
         * Examples: /destinations/rocky-point/things-to-do
         */
        childPage: {
            path: "/[slug]/[subPage]",
            url: (slug: string, page: string) => {
                const baseSlug = slug.startsWith("/") ? `${slug}` : `/${slug}`;
                return `${baseSlug}/${page}`;
            },
        } as RouteWithTwoSlugs & {helpers: typeof attractionHelpers},

        /**
         * Attraction list page: /destinations/[slug]/[attraction]
         * Examples: /destinations/rocky-point/things-to-do
         */
        attraction: {
            path: "/[slug]/[attraction]",
            url: (slug: string, attraction: string) => {
                const baseSlug = slug.startsWith("/") ? `${slug}` : `/${slug}`;
                return `${baseSlug}/${attraction}`;
            },
            helpers: attractionHelpers,
        } as RouteWithTwoSlugs & {helpers: typeof attractionHelpers},

        /**
         * Attraction detail page: /destinations/[slug]/[attraction]/[attractionSlug]
         * Examples: /destinations/rocky-point/things-to-do/senorita-rita-sunset-cruise
         */
        attractionDetail: {
            path: "/[slug]/[attraction]/[attractionSlug]",
            url: (destinationSlug: string, attraction: string, attractionSlug: string) => {
                const baseSlug = destinationSlug.startsWith("/") ? `${destinationSlug}` : `/${destinationSlug}`;
                return `${baseSlug}/${attraction}/${attractionSlug}`;
            },
            helpers: attractionHelpers,
        } as RouteWithThreeSlugs & {helpers: typeof attractionHelpers},

        /**
         * Destination blog page: /destinations/[slug]/blog
         * Examples: /destinations/rocky-point/blog
         */
        blog: {
            path: "/[slug]/blog",
            url: (slug: string) => (slug.startsWith("/") ? `${slug}/blog` : `/${slug}/blog`),

            /**
             * Destination blog post detail page: /[slug]/[postSlug]
             * Blog posts are now at the same level as communities (no /blog/ prefix)
             * Examples: /rocky-point/top-activities
             */
            detail: {
                path: "/[slug]/[postSlug]",
                url: (destinationSlug: string, postSlug: string) => {
                    const baseSlug = destinationSlug.startsWith("/") ? `${destinationSlug}` : `/${destinationSlug}`;
                    return `${baseSlug}/${postSlug}`;
                },
            } as RouteWithTwoSlugs,
        } as RouteWithSlug & {detail: RouteWithTwoSlugs},

        /**
         * Destination community archive page: /destinations/[slug]/communities (plural)
         * Examples: /destinations/rocky-point/communities
         */
        communityList: {
            path: "/[slug]/communities",
            url: (slug: string) => (slug.startsWith("/") ? `${slug}/communities` : `/${slug}/communities`),
        } as RouteWithSlug,

        /**
         * Destination community detail page: /destinations/[slug]/{community-slug}
         * Handled through [attraction] router with fallback to community lookup
         * Examples: /destinations/rocky-point/encantame-soleil
         */
        communityDetail: {
            path: "/[slug]/[communitySlug]",
            url: (destinationSlug: string, communitySlug: string) => {
                const baseSlug = destinationSlug.startsWith("/") ? `${destinationSlug}` : `/${destinationSlug}`;
                return `${baseSlug}/${communitySlug}`;
            },
            /**
             * Community URL for franchise community objects that may be a reference.
             * - `record_type === "franchiseReference"`: uses `path`
             * - otherwise: uses `slug` with the current destination slug
             */
            urlFromCommunity: (destinationSlug: string, community: CommunityLinkLike): string => {
                if (community.record_type === "franchiseReference") {
                    return normalizeHrefPath(community.path) ?? "#";
                }
                return community.slug ? routes.destinations.communityDetail.url(destinationSlug, community.slug) : "#";
            },
        } as CommunityDetailRoute,

        /**
         * Community page: /destinations/[slug]/[communitySlug]/[communityPageSlug]
         */
        communityPage: {
            path: "/[slug]/[communitySlug]/[communityPageSlug]",
            url: (destinationSlug: string, communitySlug: string, communityPageSlug: string) => {
                const baseSlug = destinationSlug.startsWith("/") ? `${destinationSlug}` : `/${destinationSlug}`;
                return `${baseSlug}/${communitySlug}/${communityPageSlug}`;
            },
        },

        /**
         * Travel information page: /destinations/[slug]/travel-information/[information]
         * Examples: /destinations/rocky-point/travel-information/getting-there
         */
        travelInformation: {
            path: "/[slug]/travel-information/[information]",
            url: (slug: string, information: string) => {
                const baseSlug = slug.startsWith("/") ? `${slug}` : `/${slug}`;
                return `${baseSlug}/travel-information/${information}`;
            },
        } as RouteWithTwoSlugs,
    },

    /**
     * Feature page: /feature
     */
    feature: {
        path: "/feature",
        url: () => "/feature",
    } as SimpleRoute,

    /**
     * Franchise page: /franchise
     */
    franchise: {
        path: "/franchise",
        url: () => "/franchise",
    } as SimpleRoute,

    /**
     * Life Time page: /life-time
     */
    lifeTime: {
        path: "/life-time",
        url: () => "/life-time",
    } as SimpleRoute,

    /**
     * Rental routes
     */
    rental: {
        /**
         * Rental detail page: /rentals/[slug]
         * Supports booking query parameters
         */
        detail: rentalRoute,
    },

    /**
     * Search page: /search
     * Supports extensive query parameters for filtering
     */
    search: searchRoute,

    /**
     * Trip routes: /trips, /trips/[id]/[tab]
     */
    trip: tripRoute,

    /**
     * Generic page route: /[slug]
     * For simple content pages
     */
    page: pageRoute,
} as const;

/**
 * Type helper to get routes type
 */
export type Routes = typeof routes;
